import React, { useEffect, useMemo, useState } from "react";
import { getMyEmployee, updateMyEmployee } from "../../api/employees";
import { mapEmployeeRow } from "../../utils/dataMappers";
import {
  EDUCATION_LEVEL_OPTIONS,
  EMERGENCY_RELATION_OPTIONS,
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  MILITARY_STATUS_OPTIONS,
  YES_NO_OPTIONS,
  completenessTone,
  optionLabel,
  requiredProfileFieldStatus,
  toDateInputValue,
} from "../../utils/profileCompleteness";
import {
  PROFILE_DECLARATION_TEXT,
  PROFILE_UPDATED_EVENT,
  profileCompletionBannerText,
  resolveProfileCompletionState,
} from "../../utils/profileCompletionLock";
import "./MyProfile.css";
import ThemeToggle from "../common/ThemeToggle";

const EMPTY_FORM = {
  legalFullName: "",
  phone: "",
  phoneSecondary: "",
  nationalId: "",
  gender: "",
  birthDate: "",
  address: "",
  maritalStatus: "",
  childrenCount: "",
  militaryStatus: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
  emergencyContactRelation: "",
  fatherJob: "",
  motherJob: "",
  siblingsCount: "",
  courses: "",
  hasChronicDisease: "",
  chronicDiseaseDetails: "",
  chronicDiseaseMedication: "",
  hasCompanyRelatives: "",
  companyRelativeDetails: "",
  careerGoal: "",
  educationLevel: "",
  educationInstitution: "",
  graduationYear: "",
};

function yesNoFromBool(value) {
  if (value === true) return "yes";
  if (value === false) return "no";
  return "";
}

function profileToForm(profile) {
  if (!profile) return { ...EMPTY_FORM };
  return {
    legalFullName: profile.legalFullName || "",
    phone: profile.phone || "",
    phoneSecondary: profile.phoneSecondary || "",
    nationalId: profile.nationalId || "",
    gender: profile.gender || "",
    birthDate: toDateInputValue(profile.birthDate),
    address: profile.address || "",
    maritalStatus: profile.maritalStatus || "",
    childrenCount:
      profile.childrenCount === null || profile.childrenCount === undefined
        ? ""
        : String(profile.childrenCount),
    militaryStatus: profile.militaryStatus || "",
    emergencyContactName: profile.emergencyContactName || "",
    emergencyContactPhone: profile.emergencyContactPhone || "",
    emergencyContactRelation: profile.emergencyContactRelation || "",
    fatherJob: profile.fatherJob || "",
    motherJob: profile.motherJob || "",
    siblingsCount:
      profile.siblingsCount === null || profile.siblingsCount === undefined
        ? ""
        : String(profile.siblingsCount),
    courses: profile.courses || "",
    hasChronicDisease: yesNoFromBool(profile.hasChronicDisease),
    chronicDiseaseDetails: profile.chronicDiseaseDetails || "",
    chronicDiseaseMedication: profile.chronicDiseaseMedication || "",
    hasCompanyRelatives: yesNoFromBool(profile.hasCompanyRelatives),
    companyRelativeDetails: profile.companyRelativeDetails || "",
    careerGoal: profile.careerGoal || "",
    educationLevel: profile.educationLevel || "",
    educationInstitution: profile.educationInstitution || "",
    graduationYear:
      profile.graduationYear === null || profile.graduationYear === undefined
        ? ""
        : String(profile.graduationYear),
  };
}

function formToPayload(form) {
  const payload = {
    legalFullName: form.legalFullName.trim() || undefined,
    phone: form.phone.trim() || undefined,
    phoneSecondary: form.phoneSecondary.trim() || undefined,
    nationalId: form.nationalId.trim() || undefined,
    gender: form.gender || undefined,
    birthDate: form.birthDate || undefined,
    address: form.address.trim() || undefined,
    maritalStatus: form.maritalStatus || undefined,
    militaryStatus: form.militaryStatus || undefined,
    emergencyContactName: form.emergencyContactName.trim() || undefined,
    emergencyContactPhone: form.emergencyContactPhone.trim() || undefined,
    emergencyContactRelation: form.emergencyContactRelation || undefined,
    fatherJob: form.fatherJob.trim() || undefined,
    motherJob: form.motherJob.trim() || undefined,
    courses: form.courses.trim() || undefined,
    careerGoal: form.careerGoal.trim() || undefined,
    educationLevel: form.educationLevel || undefined,
    educationInstitution: form.educationInstitution.trim() || undefined,
  };
  if (form.graduationYear) {
    payload.graduationYear = Number(form.graduationYear);
  }
  if (form.siblingsCount !== "") {
    payload.siblingsCount = Number(form.siblingsCount);
  }
  if (form.maritalStatus === "MARRIED" && form.childrenCount !== "") {
    payload.childrenCount = Number(form.childrenCount);
  }
  if (form.hasChronicDisease === "yes" || form.hasChronicDisease === "no") {
    payload.hasChronicDisease = form.hasChronicDisease === "yes";
  }
  if (form.hasChronicDisease === "yes") {
    payload.chronicDiseaseDetails = form.chronicDiseaseDetails.trim() || undefined;
    payload.chronicDiseaseMedication =
      form.chronicDiseaseMedication.trim() || undefined;
  }
  if (form.hasCompanyRelatives === "yes" || form.hasCompanyRelatives === "no") {
    payload.hasCompanyRelatives = form.hasCompanyRelatives === "yes";
  }
  if (form.hasCompanyRelatives === "yes") {
    payload.companyRelativeDetails =
      form.companyRelativeDetails.trim() || undefined;
  }
  return payload;
}

function Field({ label, required, className, children }) {
  return (
    <label className={`my-profile-field${className ? ` ${className}` : ""}`}>
      <span>
        {label}
        {required ? (
          <em className="my-profile-required" aria-hidden="true">
            *
          </em>
        ) : null}
      </span>
      {children}
    </label>
  );
}

export default function MyProfile() {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [declarationAccepted, setDeclarationAccepted] = useState(false);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const data = await getMyEmployee();
      const mapped = mapEmployeeRow(data);
      setProfile({ ...data, ...mapped, id: data.id });
      setForm(profileToForm({ ...data, ...mapped }));
      setDeclarationAccepted(false);
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر جلب بيانات الملف",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const liveStatus = requiredProfileFieldStatus(form);
  const filled = liveStatus.filled;
  const total = liveStatus.total;
  const percent = liveStatus.percent;
  const missing = liveStatus.missing;
  const isComplete = liveStatus.isComplete;

  const lockState = resolveProfileCompletionState(profile);
  const lockBanner = profileCompletionBannerText(lockState);

  const showMilitary = form.gender === "MALE";
  const showChildren = form.maritalStatus === "MARRIED";
  const showChronic = form.hasChronicDisease === "yes";
  const showCompanyRelatives = form.hasCompanyRelatives === "yes";

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSuccess("");
  }

  async function handleSave(event) {
    event.preventDefault();
    if (!declarationAccepted) {
      setError("يجب الموافقة على الإقرار قبل إرسال البيانات");
      return;
    }
    const incomplete = requiredProfileFieldStatus(form);
    if (!incomplete.isComplete) {
      setError(
        `أكمل البيانات المطلوبة أولاً: ${incomplete.missing.join("، ")}`,
      );
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const data = await updateMyEmployee({
        ...formToPayload(form),
        profileDeclarationAccepted: true,
      });
      const mapped = mapEmployeeRow(data);
      const nextProfile = { ...data, ...mapped, id: data.id };
      setProfile(nextProfile);
      setForm(profileToForm(nextProfile));
      setDeclarationAccepted(false);
      setSuccess("تم حفظ البيانات وإرسال إشعار لشؤون الموظفين");
      window.dispatchEvent(
        new CustomEvent(PROFILE_UPDATED_EVENT, { detail: nextProfile }),
      );
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر حفظ البيانات",
      );
    } finally {
      setSaving(false);
    }
  }

  const jobBits = useMemo(() => {
    if (!profile) return [];
    return [
      profile.code,
      profile.jobTitle,
      profile.departmentName || profile.department?.name,
      profile.branchName || profile.branch?.name,
    ].filter(Boolean);
  }, [profile]);

  if (loading) {
    return (
      <div className="my-profile" dir="rtl">
        <p className="my-profile-muted">جاري تحميل ملفك الشخصي...</p>
      </div>
    );
  }

  if (!profile?.id) {
    return (
      <div className="my-profile" dir="rtl">
        <div className="ess-home-header">
          <h1 className="ess-home-title">ملفي الشخصي</h1>
          <p className="ess-home-subtitle">
            لا يوجد ملف موظف مرتبط بحسابك. تواصل مع شؤون الموظفين.
          </p>
        </div>
        {error ? <p className="my-profile-error">{error}</p> : null}
      </div>
    );
  }

  return (
    <div className="my-profile" dir="rtl">
      <div className="ess-home-header">
        <h1 className="ess-home-title">ملفي الشخصي</h1>
        <p className="ess-home-subtitle">
          استكمال البيانات الشخصية إجباري. كل الحقول المطلوبة لازم تتعبّى قبل
          الحفظ. أي حفظ يصل إشعار لشؤون الموظفين.
        </p>
      </div>

      <section className="my-profile-card my-profile-theme-card">
        <div className="my-profile-theme-copy">
          <h3>المظهر</h3>
          <p className="my-profile-hint">
            اختار فاتح أو داكن. الاختيار يدوي ويتسجل على الجهاز، ومش تابع لإعدادات النظام.
          </p>
        </div>
        <ThemeToggle />
      </section>

      {lockBanner ? (
        <section
          className={`my-profile-lock-banner${
            lockState.isLocked ? " my-profile-lock-banner--locked" : ""
          }`}
        >
          <p>{lockBanner}</p>
        </section>
      ) : null}

      <section className={`my-profile-progress my-profile-progress--${completenessTone(percent)}`}>
        <div className="my-profile-progress-main">
          <div>
            <h2>اكتمال الملف</h2>
            <p>
              {filled} من {total} بند مطلوب
              {isComplete ? " — الملف مكتمل" : ""}
            </p>
          </div>
          <div className="my-profile-percent">{percent}%</div>
        </div>
        <div className="my-profile-progress-track">
          <div
            className="my-profile-progress-fill"
            style={{ width: `${percent}%` }}
          />
        </div>
        {missing.length > 0 ? (
          <p className="my-profile-missing">
            <strong>ناقص:</strong> {missing.join(" · ")}
          </p>
        ) : (
          <p className="my-profile-complete-note">
            كل البيانات المطلوبة مكتملة.
          </p>
        )}
      </section>

      <section className="my-profile-card">
        <h3>بيانات الوظيفة</h3>
        <p className="my-profile-readonly-name">{profile.name}</p>
        <p className="my-profile-readonly-meta">{jobBits.join(" · ")}</p>
        <p className="my-profile-hint">
          المسمى والإدارة والفرع يحدّدها شؤون الموظفين ولا يمكن تعديلها من هنا.
        </p>
      </section>

      <form className="my-profile-card" onSubmit={handleSave}>
        <h3>البيانات الشخصية</h3>
        <div className="my-profile-grid">
          <Field label="الاسم بالكامل كما في الرقم القومي" required>
            <input
              value={form.legalFullName}
              onChange={(e) => updateField("legalFullName", e.target.value)}
              maxLength={150}
            />
          </Field>
          <Field label="الرقم القومي" required>
            <input
              value={form.nationalId}
              onChange={(e) => updateField("nationalId", e.target.value)}
              maxLength={30}
            />
          </Field>
          <Field label="رقم الهاتف" required>
            <input
              value={form.phone}
              onChange={(e) => updateField("phone", e.target.value)}
              maxLength={20}
            />
          </Field>
          <Field label="رقم إضافي">
            <input
              value={form.phoneSecondary}
              onChange={(e) => updateField("phoneSecondary", e.target.value)}
              maxLength={20}
            />
          </Field>
          <Field label="النوع" required>
            <select
              value={form.gender}
              onChange={(e) => updateField("gender", e.target.value)}
            >
              <option value="">اختر</option>
              {GENDER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="تاريخ الميلاد" required>
            <input
              type="date"
              value={form.birthDate}
              onChange={(e) => updateField("birthDate", e.target.value)}
            />
          </Field>
          <Field label="الحالة الاجتماعية" required>
            <select
              value={form.maritalStatus}
              onChange={(e) => updateField("maritalStatus", e.target.value)}
            >
              <option value="">اختر</option>
              {MARITAL_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>
          {showChildren ? (
            <Field label="عدد الأبناء" required>
              <input
                type="number"
                min="0"
                max="20"
                value={form.childrenCount}
                onChange={(e) => updateField("childrenCount", e.target.value)}
              />
            </Field>
          ) : null}
          {showMilitary ? (
            <Field label="الموقف من التجنيد" required>
              <select
                value={form.militaryStatus}
                onChange={(e) => updateField("militaryStatus", e.target.value)}
              >
                <option value="">اختر</option>
                {MILITARY_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
          <Field label="العنوان" required>
            <input
              value={form.address}
              onChange={(e) => updateField("address", e.target.value)}
              maxLength={500}
            />
          </Field>
        </div>

        <h3>قريب الدرجة الأولى (للطوارئ فقط)</h3>
        <div className="my-profile-grid">
          <Field label="الاسم" required>
            <input
              value={form.emergencyContactName}
              onChange={(e) =>
                updateField("emergencyContactName", e.target.value)
              }
              maxLength={150}
            />
          </Field>
          <Field label="رقم الهاتف" required>
            <input
              value={form.emergencyContactPhone}
              onChange={(e) =>
                updateField("emergencyContactPhone", e.target.value)
              }
              maxLength={20}
            />
          </Field>
          <Field label="صلة القرابة" required>
            <select
              value={form.emergencyContactRelation}
              onChange={(e) =>
                updateField("emergencyContactRelation", e.target.value)
              }
            >
              <option value="">اختر</option>
              {EMERGENCY_RELATION_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <h3>بيانات الأسرة</h3>
        <div className="my-profile-grid">
          <Field label="وظيفة الأب" required>
            <input
              value={form.fatherJob}
              onChange={(e) => updateField("fatherJob", e.target.value)}
              maxLength={100}
            />
          </Field>
          <Field label="وظيفة الأم" required>
            <input
              value={form.motherJob}
              onChange={(e) => updateField("motherJob", e.target.value)}
              maxLength={100}
            />
          </Field>
          <Field label="عدد الإخوة / الأخوات" required>
            <input
              type="number"
              min="0"
              max="20"
              value={form.siblingsCount}
              onChange={(e) => updateField("siblingsCount", e.target.value)}
            />
          </Field>
        </div>

        <h3>التدريب والصحة والعمل</h3>
        <div className="my-profile-grid">
          <Field label="هل تعاني من أمراض مزمنة؟" required>
            <select
              value={form.hasChronicDisease}
              onChange={(e) => updateField("hasChronicDisease", e.target.value)}
            >
              <option value="">اختر</option>
              {YES_NO_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>
          {showChronic ? (
            <>
              <Field label="ما هو المرض؟" required>
                <input
                  value={form.chronicDiseaseDetails}
                  onChange={(e) =>
                    updateField("chronicDiseaseDetails", e.target.value)
                  }
                  maxLength={500}
                />
              </Field>
              <Field label="العلاج الذي تتناوله" required>
                <input
                  value={form.chronicDiseaseMedication}
                  onChange={(e) =>
                    updateField("chronicDiseaseMedication", e.target.value)
                  }
                  maxLength={500}
                />
              </Field>
            </>
          ) : null}
          <Field label="هل لديك أقارب في الشركة؟" required>
            <select
              value={form.hasCompanyRelatives}
              onChange={(e) =>
                updateField("hasCompanyRelatives", e.target.value)
              }
            >
              <option value="">اختر</option>
              {YES_NO_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>
          {showCompanyRelatives ? (
            <Field label="الاسم ودرجة القرابة" required>
              <input
                value={form.companyRelativeDetails}
                onChange={(e) =>
                  updateField("companyRelativeDetails", e.target.value)
                }
                maxLength={500}
              />
            </Field>
          ) : null}
          <Field label="الوظيفة التي تطمح للعمل فيها مستقبلاً" required>
            <input
              value={form.careerGoal}
              onChange={(e) => updateField("careerGoal", e.target.value)}
              maxLength={200}
            />
          </Field>
          <Field label="الكورسات أو التدريبات" required className="my-profile-field-wide">
            <textarea
              rows={3}
              value={form.courses}
              onChange={(e) => updateField("courses", e.target.value)}
              maxLength={2000}
            />
          </Field>
        </div>

        <h3>المؤهل الدراسي</h3>
        <div className="my-profile-grid">
          <Field label="المؤهل" required>
            <select
              value={form.educationLevel}
              onChange={(e) => updateField("educationLevel", e.target.value)}
            >
              <option value="">اختر</option>
              {EDUCATION_LEVEL_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </Field>
          <Field label="جهة التعليم" required>
            <input
              value={form.educationInstitution}
              onChange={(e) =>
                updateField("educationInstitution", e.target.value)
              }
              maxLength={150}
            />
          </Field>
          <Field label="سنة التخرج" required>
            <input
              type="number"
              min="1950"
              max="2035"
              value={form.graduationYear}
              onChange={(e) => updateField("graduationYear", e.target.value)}
            />
          </Field>
        </div>

        <section className="my-profile-declaration">
          <h3>إقرار الموظف</h3>
          <p className="my-profile-declaration-text">{PROFILE_DECLARATION_TEXT}</p>
          <label className="my-profile-declaration-check">
            <input
              type="checkbox"
              checked={declarationAccepted}
              onChange={(e) => {
                setDeclarationAccepted(e.target.checked);
                setSuccess("");
                setError("");
              }}
            />
            <span>أوافق على الإقرار أعلاه</span>
          </label>
        </section>

        {error ? <p className="my-profile-error">{error}</p> : null}
        {success ? <p className="my-profile-success">{success}</p> : null}
        <button
          type="submit"
          className="my-profile-save"
          disabled={saving || !declarationAccepted || !isComplete}
        >
          {saving ? "جاري الحفظ..." : "حفظ البيانات وإشعار شؤون الموظفين"}
        </button>
      </form>
    </div>
  );
}

export function ProfileReadOnlyFields({ profile }) {
  if (!profile) return null;
  const rows = [
    ["الاسم كما في البطاقة", profile.legalFullName],
    ["الهاتف", profile.phone],
    ["رقم إضافي", profile.phoneSecondary],
    ["الرقم القومي", profile.nationalId],
    ["النوع", optionLabel(GENDER_OPTIONS, profile.gender)],
    ["تاريخ الميلاد", toDateInputValue(profile.birthDate) || "—"],
    ["العنوان", profile.address],
    ["الحالة الاجتماعية", optionLabel(MARITAL_STATUS_OPTIONS, profile.maritalStatus)],
    ["عدد الأبناء", profile.childrenCount],
    ["التجنيد", optionLabel(MILITARY_STATUS_OPTIONS, profile.militaryStatus)],
    ["قريب الطوارئ", profile.emergencyContactName],
    ["هاتف الطوارئ", profile.emergencyContactPhone],
    ["صلة القرابة", profile.emergencyContactRelation],
    ["وظيفة الأب", profile.fatherJob],
    ["وظيفة الأم", profile.motherJob],
    ["عدد الإخوة / الأخوات", profile.siblingsCount],
    ["كورسات / تدريبات", profile.courses],
    [
      "أمراض مزمنة",
      profile.hasChronicDisease === true
        ? "نعم"
        : profile.hasChronicDisease === false
          ? "لا"
          : "—",
    ],
    ["المرض", profile.chronicDiseaseDetails],
    ["العلاج", profile.chronicDiseaseMedication],
    [
      "أقارب في الشركة",
      profile.hasCompanyRelatives === true
        ? "نعم"
        : profile.hasCompanyRelatives === false
          ? "لا"
          : "—",
    ],
    ["درجة القرابة", profile.companyRelativeDetails],
    ["الوظيفة المستهدفة", profile.careerGoal],
    ["البنك", profile.bankName],
    ["رقم الحساب", profile.bankAccountNumber],
    ["IBAN", profile.iban],
    ["رقم التأمينات", profile.socialInsuranceNumber],
    ["المؤهل", profile.educationLevel],
    ["جهة التعليم", profile.educationInstitution],
    ["سنة التخرج", profile.graduationYear],
  ];
  return (
    <dl className="my-profile-readonly-grid">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value || "—"}</dd>
        </div>
      ))}
    </dl>
  );
}
